import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from 'features/common/hooks/useColumnSelection';
import { CompedTicketsTableProps } from '../types';
import { Page } from 'features/PunchupPages/Orders/types';
import {
	PunchupBreakdownCell,
	PunchupDataRow,
	PunchupEntityCell,
	PunchupNameCell,
	PunchupTableEmptyRow,
	PunchupTableLoadingRow,
	punchupTableStyles,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupLineItemList } from 'features/common/components/Punchup/primitives/display/PunchupLineItemList';
import { PunchupTag } from 'features/common/components/Punchup/primitives/display/PunchupTag';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { countTicketsByType } from 'features/common/utils/compedTickets';
import { getCompStatusLabel, getCompStatusBackground, isCancellableComp } from './statusColors';
import localStyles from './PunchupCompedTicketsTable.module.css';

interface PunchupCompedTicketsTableBodyProps extends CompedTicketsTableProps {
	visibleDefs: ColumnDef[];
	/** Render the sticky actions column cell (gated together with the header — see PunchupCompedTicketsTable). */
	hasActionsColumn: boolean;
	/** True while the initial fetch is in flight (data === undefined). */
	isLoading?: boolean;
	/** True when a search term or non-default filter is active — switches the empty body to "no results" copy. */
	hasActiveQuery?: boolean;
}

export const PunchupCompedTicketsTableBody: FC<PunchupCompedTicketsTableBodyProps> = ({
	compedTicketsQueryObject,
	openCancelModal,
	visibleDefs,
	hasActionsColumn,
	isLoading,
	hasActiveQuery,
}) => {
	const { t } = useTranslation();
	const { data: compedTickets } = compedTicketsQueryObject;
	// Resolve the type colour from the shared source (the event's ticket types), not the per-row
	// `ticket_type_color` — the /orders comped payload null-fills it, which showed grey tags here.
	const { resolveByName } = useTicketTypeColors();

	// Loading row only fires on the INITIAL fetch (no data yet). Once data has
	// arrived, even a re-fetch keeps the rows visible — the load-more spinner
	// is the unobtrusive indicator for paginated fetches.
	if (isLoading || !compedTickets) {
		return (
			<tbody>
				<PunchupTableLoadingRow />
			</tbody>
		);
	}

	if (compedTickets.pages[0].data.length === 0) {
		// Two distinct empty states: a search/filter that matched nothing vs. a show with no comps issued yet.
		// Collapsing them would make a failed name/email lookup read as "no comps have ever been sent".
		const emptyText = hasActiveQuery
			? t('compedTickets-empty-no-results', 'No comped tickets match that name or email.')
			: t('compedTickets-empty', 'No comps issued yet — use Send Tickets to add guests to the list.');
		return (
			<tbody>
				<PunchupTableEmptyRow text={emptyText} />
			</tbody>
		);
	}

	return (
		<tbody>
			{compedTickets.pages.map((page: Page) =>
				page?.data?.map(({ User: { email, full_name }, id, tickets, tickets_name, transaction_type, quantity }) => (
					<PunchupDataRow key={id}>
						{visibleDefs.map((col) => {
							switch (col.key) {
								case 'email':
									return <PunchupEntityCell key="email">{email}</PunchupEntityCell>;
								case 'firstName':
									return <PunchupNameCell key="firstName">{full_name.split(' ')[0]}</PunchupNameCell>;
								case 'lastName':
									return <PunchupNameCell key="lastName">{full_name.split(' ')[1]}</PunchupNameCell>;
								case 'ticketType': {
									// Per-type count breakdown (e.g. "2× VIP" / "1× GA") stacked on
									// separate lines, each type rendered as a colour-tinted tag. No price —
									// a comp charges the recipient nothing.
									return (
										<PunchupBreakdownCell key="ticketType">
											<PunchupLineItemList
												items={Object.entries(countTicketsByType(tickets, tickets_name)).map(([name, count]) => ({
													label: <PunchupTicketTypeTag name={name} color={resolveByName(name)} />,
													sortKey: name,
													count,
												}))}
											/>
										</PunchupBreakdownCell>
									);
								}
								case 'status': {
									const status = getCompStatusLabel(transaction_type);
									return (
										<td key="status">
											<PunchupTag
												size="sm"
												label={t(status.key, status.fallback)}
												background={getCompStatusBackground(transaction_type)}
											/>
										</td>
									);
								}
								case 'quantity':
									return <td key="quantity">{quantity}</td>;
								default:
									return null;
							}
						})}

						{hasActionsColumn && (
							<td className={punchupTableStyles.actionsCell}>
								{isCancellableComp(transaction_type) && (
									<button className={localStyles.cancelButton} type="button" onClick={() => openCancelModal(id)}>
										{t('compedTickets-row-cancel', 'Cancel')}
									</button>
								)}
							</td>
						)}
					</PunchupDataRow>
				))
			)}
		</tbody>
	);
};
